/**
 * Notification destination link resolver.
 *
 * The single, shared mapping (BR-75) from a notification's `referenceType`/
 * `referenceId` to the landlord-router-relative path it should deep-link to.
 * Both apps/api (push payload + email actionUrl) and apps/landlord (any
 * caller that needs to derive the same destination) import this — no caller
 * re-derives the mapping.
 *
 * Pure, no I/O, no env: callers own turning this into an absolute URL (e.g.
 * `${env.LANDLORD_APP_BASE_URL}${path}`) since only the caller knows its
 * app-base-URL / router basename.
 */
/** Landlord-router-relative fallback path when no specific destination applies. */
export const NOTIFICATION_FALLBACK_PATH = '/dashboard';
/**
 * Resolve the landlord-router-relative destination path for a notification.
 *
 * Returns a path with no `/landlord` basename and no origin — the caller
 * prepends whatever it needs (SPA router base, absolute app URL, etc).
 */
export function resolveNotificationPath(referenceType, referenceId) {
    if (!referenceId) {
        return NOTIFICATION_FALLBACK_PATH;
    }
    switch (referenceType) {
        case 'lead':
            return `/leasing/leads/${referenceId}`;
        case 'lease':
            return `/leases/${referenceId}`;
        // BR-MF-01 (listing-syndication iter4, ShowMojo) — listing_period_expiring's referenceType (BR-93).
        // Path verified against apps/landlord/src/App.tsx's `/leasing/listings/:id` route.
        case 'listing':
            return `/leasing/listings/${referenceId}`;
        // MF-BR-77/MF-BR-75 — the four classes this change routes. Paths verified against
        // apps/landlord/src/App.tsx at compile time (line numbers as of 54ea293d).
        case 'expense':
            // App.tsx:510 — /expenses/:id
            return `/expenses/${referenceId}`;
        case 'agent_response_review_log':
            // App.tsx:796 — /agent/review-queue. NOT instance-scoped: no per-review route exists.
            // A route that does not exist is worse than the list page — do not invent one.
            return '/agent/review-queue';
        // BR-ESC-NOTIFY / RA-ESC-NOTIFY — agent-escalation-notifier.ts's fallback reference for
        // an escalation on a case kind with no dedicated detail route (neither leasing's Lead nor
        // repair's RepairRequest). NOT instance-scoped by referenceId: the escalations queue page
        // is the only surface, mirroring agent_response_review_log's list-page convention above.
        case 'case_escalation':
            return `/agent/escalations?escalation=${referenceId}`;
        case 'channel_message':
        case 'email_message':
            // App.tsx:463 — /email. NOT instance-scoped: no /email/:threadId route exists. The retired
            // triage queue had its own per-message route; channel_message now lands on the same inbox.
            return '/email';
        // MF-BR-77 — the remaining registered classes this change routes. Paths VERIFIED against
        // apps/landlord/src/App.tsx. All four are list pages (NOT instance-scoped by referenceId):
        // no route keyed by a linked_bank_account / bank_transaction / payment / subscription /
        // tax_form_1099k id exists. A route that does not exist is worse than the list page —
        // do not invent one.
        case 'linked_bank_account':
            // App.tsx:589 — /settings/linked-accounts (SettingsLinkedAccountsPage: Plaid-linked
            // bank accounts). Covers external_payment_unmatched, bank_reconnection_required,
            // bank_connection_error, bank_connection_expiring.
            return '/settings/linked-accounts';
        case 'bank_transaction':
        case 'payment':
            // App.tsx:364 — /external-payments (ExternalPaymentsHubPage: "central hub for
            // reviewing external payment detection results" — unmatched/auto-applied transactions
            // and payments pending manual review). Covers external_payment_auto_applied,
            // external_payment_detected, payment_manual_review.
            return '/external-payments';
        case 'subscription':
            // App.tsx:417 — /settings/billing (BillingPage: subscription management). Covers
            // subscription_expired.
            return '/settings/billing';
        case 'tax_form_1099k':
            // App.tsx:380 — /reports/tax (TaxReportsPage). Covers tax_form_available.
            return '/reports/tax';
        // RA-REQUEST-EVENT — repair_request_created's referenceType. Path verified against
        // apps/landlord/src/App.tsx:559 — /repairs/:id (instance-scoped, mirrors the
        // repair-request-triage attention provider's own actionUrl convention).
        case 'repair_request':
            return `/repairs/${referenceId}`;
        // NOTE: 'communication_log' (message_delivery_failed) is deliberately NOT routed here.
        // Its referenceType is itself ambiguous at the producer level (tenant-invite.service.ts
        // writes 'tenant_invite'; messaging.service.ts writes 'communication_log') — a
        // pre-existing gap this change does not resolve. No route exists for either value, so it
        // stays on the /dashboard fallback rather than guessing at a destination for an
        // unresolved reference.
        default:
            return NOTIFICATION_FALLBACK_PATH;
    }
}
//# sourceMappingURL=notification-links.js.map